import { AfterContentChecked, Component, ElementRef, ErrorHandler, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { parseJSON } from 'date-fns';
import { catchError, map, tap } from 'rxjs/operators';
import { WebSocketSubject } from 'rxjs/webSocket';
import { BlockConfigurationObject } from 'src/app/block-configuration-object.model';
import { GeneralService } from 'src/app/general.service';
import { LocalisationService } from 'src/app/localisation.service';
import { MatchLiveData } from 'src/app/match-live-data.model';
import { IsLivePipe } from 'src/app/match-pipes/is-live.pipe';
import { IsPausedPipe } from 'src/app/match-pipes/is-paused.pipe';
import { Match } from 'src/app/match.model';
import { MatchService } from 'src/app/match.service';
import { SportDataService } from 'src/app/sport-data.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-match-page-header-block',
  templateUrl: './match-page-header-block.component.html',
  styleUrls: ['./match-page-header-block.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MatchPageHeaderBlockComponent implements OnInit, OnDestroy, OnChanges, AfterContentChecked {

  @Input() configurationObject: BlockConfigurationObject;
	@ViewChild("blockBody") blockBody: ElementRef;

	awayBeforeHome = false;
	awayScoreJustChanged = false;
	blockDomain = environment.blockDomain;
	displayMaximumWidths: Record<string, number> = {
		"x-compact": 350,
		compact: 500,
		"less-compact": 600,
		medium: 700,
		large: 800,
	};
	displayMode = "";
	gamesError = false;
	gamesLoading = true;
	homeScoreJustChanged = false;
	liveDataSocket$: WebSocketSubject<any>;
	liveSocketError = false;
	match: Match | null = null;
	blockInitStarted = false;




	constructor(
		private errorHandler: ErrorHandler,
		public matchService: MatchService,
		private sportDataService: SportDataService,
		public generalService: GeneralService,
		private el: ElementRef,
    public localisationService: LocalisationService,
    private isLivePipe: IsLivePipe,
    private isPausedPipe: IsPausedPipe,
	) {}

	prepareBlock(): void {
		this.blockInitStarted = true;
		this.generalService.initialiseBlock(this.el);
		if (!this.configurationObject.matchFirstTeam || this.configurationObject.matchFirstTeam === "away") {
			this.awayBeforeHome = true;
		}

		if (typeof window !== "undefined" && typeof document !== "undefined") {
			window.addEventListener("resize", this.checkCompact.bind(this));
		}

		this.matchService.getGeoData().then(() => {
			this.retrieveSingleGame(this.configurationObject.matchID || "");
		}, () => {
			this.retrieveSingleGame(this.configurationObject.matchID || "");
		});
	}

	ngOnInit(): void {
		if (this.configurationObject && !this.blockInitStarted) {
			this.prepareBlock();
		}

	}

	ngAfterContentChecked(): void {

		const element = this.el?.nativeElement;
		if (element && !this.displayMode) {
			this.displayMode = "full";
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					this.displayMode += ` ${level}`;
				}
			});

			const blockParent = element.parentNode;
			if (blockParent && typeof MutationObserver !== "undefined") {
				new MutationObserver(() => {
					if (this.el.nativeElement === null) {
						if (this.liveDataSocket$ && !this.liveDataSocket$.closed) {
							this.liveDataSocket$.complete();
						}
					}
				}).observe(blockParent, {childList: true, subtree: true});
			}
		}

	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.blockInitStarted && changes.configurationObject.currentValue) {
			this.prepareBlock();
		}
	}

	ngOnDestroy(): void {
		if (this.liveDataSocket$ && !this.liveDataSocket$.closed) {
			this.liveDataSocket$.complete();
		}
	}

	checkCompact(): void {
		let modeString = "full";
		const element = this.blockBody.nativeElement;
		if (element) {
			Object.keys(this.displayMaximumWidths).forEach(level => {
				if (element && element.offsetWidth <= this.displayMaximumWidths[level]) {
					modeString += ` ${level}`;
				}
			});
		}


		this.displayMode = modeString;
	};

	updateLiveData = (newLiveRow: MatchLiveData): void => {

		try {
			// check that data was received and that is still for the displayed match
			// (in case navigation was made between request and response)
			// special handling of tennis detailed stats objects
			if (newLiveRow.stat_type !== "detailed") {
				if (this.match && newLiveRow.SIMatchID === this.configurationObject.matchID) {


					// if this data is later in the match than any other available, or is tied for latest
					// (and isn't, for example, a correction to a previous event)
					if (this.match.LiveData
						&& newLiveRow.sr_event_sequence >= this.match.LiveData.sr_event_sequence) {

						// highlight scores that have just changed, if any

						if (newLiveRow.homeScore !== this.match.LiveData.homeScore
							|| newLiveRow.homeRuns !== this.match.LiveData.homeRuns
							|| newLiveRow.homeWickets !== this.match.LiveData.homeWickets) {
							this.homeScoreJustChanged = true;
							// console.log('timeout called')
							setTimeout(() => this.homeScoreJustChanged = false, 1000);
						}
						if (newLiveRow.awayScore !== this.match.LiveData.awayScore
							|| newLiveRow.awayRuns !== this.match.LiveData.awayRuns
							|| newLiveRow.awayWickets !== this.match.LiveData.awayWickets) {
							this.awayScoreJustChanged = true;
							// console.log('timeout called')
							setTimeout(() => this.awayScoreJustChanged = false, 1000);
						}


						this.match.LiveData = newLiveRow;

						this.gamesLoading = false;
					}
				}
			}
		} catch (err) {
			if (err !== "API_CALL_FAILED") {
				this.errorHandler.handleError(err);
			}
			this.gamesError = true;
		}
	};

	prepareMatchData = (): void => {
		try {


			if (this.match && this.match.MatchData) {
				this.match.MatchData.UserDate = parseJSON(this.match.MatchData.Date);
				if (this.isLivePipe.transform(this.match)
				|| this.isPausedPipe.transform(this.match)) {
					this.connectToLiveStream();
				}
			}

      this.gamesLoading = false;

		} catch (err) {
			this.errorHandler.handleError(err);

			this.gamesError = true;
		}

	};

	connectToLiveStream = () => {
		this.liveDataSocket$ = this.sportDataService.connectToMatchPushStream(this.configurationObject.matchID);
		console.log("About to connect to socket (Match Page Header Block)");
		const liveData$ = this.liveDataSocket$.pipe(
			map((row: MatchLiveData) => {
				// console.log(row);
				this.updateLiveData(row);
				return row;
			}),
			tap({
				error: error => {
					if (error?.type === "close") {
						console.log("[Match Page Header Block Socket] Connection Closed");
						this.liveSocketError = true;
					} else {
						this.errorHandler.handleError("[Match Page Header Block Socket] Error:" +
							JSON.stringify(error, ["message", "arguments", "type", "name"]))
					};
				},
				complete: () => {
					console.log("[Match Page Header Block Socket] Connection Closed");
					this.liveSocketError = true;
				}
			})
		);

		liveData$.subscribe();
	};

	convertRound = (roundString: string): string => {
		if (roundString === "0") {
			return "Group Stage";
		}
		// replace underscores with spaces, and capitalise the first word
		return roundString.replace(/_/g, " ").replace(/^[a-z]/,
			(txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
	};

	retrieveSingleGame = (matchid: string): void => {
		this.gamesLoading = true;
		this.gamesError = false;

		const match$ = this.sportDataService.getPreMatchData(matchid);
		const liveData$ = this.sportDataService.getLiveMatchData(matchid);
		match$.subscribe(
			(match) => {
				this.match = match;
				liveData$.subscribe(
					(liveData) => {
						this.match.LiveData = liveData;
						this.prepareMatchData();
					}, () => {
						this.prepareMatchData();
					}
				);
			}, () => {
				this.gamesError = true;
			}
		);
		

	};

	reloadSingleGame = (matchID: string): void => {
		this.retrieveSingleGame(matchID);
	};

}
